import { useCallback, useRef, useState, type RefCallback } from 'react';

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Hook that tracks the content dimensions of an element via ResizeObserver.
 *
 * Returns a **callback ref** to attach to the element plus its measured size.
 * Using a callback ref (instead of a passed-in ref object) is deliberate: the
 * observer is (re)attached whenever the actual DOM node changes — e.g. when a
 * component swaps its rendered subtree (placeholder -> real content). A plain
 * `useEffect([ref])` would keep observing a stale/detached node, leaving size
 * stuck at the placeholder's value and the consumer (e.g. the viz canvas) blank
 * until some unrelated re-render nudged it. See CoordinateSystem.
 *
 * Starts at { width: 0, height: 0 } until the observer fires (within a frame).
 */
export function useContainerSize(): {
  ref: RefCallback<HTMLElement | null>;
  size: ContainerSize;
} {
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback<RefCallback<HTMLElement | null>>((node) => {
    // Tear down any previous observation (node changed or unmounted).
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    // Measure immediately so we don't wait a frame when the node already has a
    // size (avoids a transient 0-width render of the new node).
    const rect = node.getBoundingClientRect();
    setSize((prev) => {
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      return prev.width === w && prev.height === h ? prev : { width: w, height: h };
    });

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize((prev) => {
        const w = Math.round(width);
        const h = Math.round(height);
        return prev.width === w && prev.height === h ? prev : { width: w, height: h };
      });
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { ref, size };
}
